import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * SECURITY: Session Timeout Hook
 * Implements 30-minute inactivity timeout for HIPAA compliance
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll)
 * - Shows warning 5 minutes before timeout
 * - Auto-logout on timeout
 * - Resets timer on any activity
 */
export function useSessionTimeout({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onTimeout,
  portalType = 'hospital' // 'hospital', 'admin', 'contractor', 'supplier'
}) {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningMinutes * 60);
  const countdownRef = useRef(null);

  const getTokenKey = useCallback(() => {
    switch (portalType) {
      case 'hospital': return 'hospital_token';
      case 'admin': return 'sterilemate_token';
      case 'contractor': return 'contractor_token';
      case 'supplier': return 'supplier_token';
      default: return 'hospital_token';
    }
  }, [portalType]);

  const getLoginPath = useCallback(() => {
    switch (portalType) {
      case 'hospital': return '/hospital/login';
      case 'admin': return '/login';
      case 'contractor': return '/contractor/login';
      case 'supplier': return '/supplier/login';
      default: return '/hospital/login';
    }
  }, [portalType]);

  const handleLogout = useCallback(() => {
    // Clear all tokens and session data
    const tokenKey = getTokenKey();
    localStorage.removeItem(tokenKey);
    
    // Clear related session data
    if (portalType === 'hospital') {
      localStorage.removeItem('hospital_user');
      localStorage.removeItem('hospital_health_system');
    } else if (portalType === 'admin') {
      localStorage.removeItem('sterilemate_user');
    }

    // Log the timeout event
    console.log(`[SECURITY] Session timeout - ${portalType} user logged out due to inactivity`);

    // Custom callback
    if (onTimeout) {
      onTimeout();
    }

    // Show message and redirect
    toast.error('Session expired due to inactivity. Please log in again.', {
      duration: 5000
    });

    navigate(getLoginPath());
  }, [getTokenKey, getLoginPath, navigate, onTimeout, portalType]);

  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    // Hide warning if shown
    setShowWarning(false);
    setRemainingTime(warningMinutes * 60);

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingTime(warningMinutes * 60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast.warning(`Your session will expire in ${warningMinutes} minutes due to inactivity.`, {
        duration: 10000
      });
    }, warningMs);

    // Set logout timer
    timeoutRef.current = setTimeout(handleLogout, timeoutMs);
  }, [timeoutMinutes, warningMinutes, handleLogout]);

  const extendSession = useCallback(() => {
    resetTimer();
    setShowWarning(false);
    toast.success('Session extended successfully');
  }, [resetTimer]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem(getTokenKey());
    if (!token) return;

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Debounce activity tracking
    let activityTimeout = null;
    const handleActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
        if (!showWarning) {
          resetTimer();
        }
      }, 1000);
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [getTokenKey, resetTimer, showWarning]);

  return {
    showWarning,
    remainingTime,
    extendSession,
    formatTime: (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  };
}

export default useSessionTimeout;
